import { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import axiosInstance from '../../../helpers/axios';
import { formatCurrency, formatPercent } from '../utils/format';
import { fibeTokens } from '../../../theme';

const summaryCardSx = {
    border: fibeTokens.density.cardBorder,
    borderRadius: `${fibeTokens.density.cardRadius}px`,
    padding: '12px 16px',
    minHeight: 96,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: fibeTokens.density.cardShadow,
    transition: 'transform 200ms ease, box-shadow 200ms ease',
    '&:hover': {
        transform: fibeTokens.density.cardHoverTransform,
        boxShadow: fibeTokens.density.cardHoverShadow,
    },
};

const CARD_GRADIENTS = {
    green: 'linear-gradient(135deg, #D1FAE5 0%, #FFFFFF 70%)',
    blue: 'linear-gradient(135deg, #DBEAFE 0%, #FFFFFF 70%)',
    amber: 'linear-gradient(135deg, #FEF3C7 0%, #FFFFFF 70%)',
    red: `linear-gradient(135deg, ${fibeTokens.color.primaryTintBorder} 0%, #FFFFFF 70%)`,
    purple: 'linear-gradient(135deg, #F3E8FF 0%, #FFFFFF 70%)',
    grey: 'linear-gradient(135deg, #F1F5F9 0%, #FFFFFF 70%)',
    teal: 'linear-gradient(135deg, #CCFBF1 0%, #FFFFFF 70%)',
    rose: 'linear-gradient(135deg, #FCE7F3 0%, #FFFFFF 70%)',
};

function SummaryCardIcon({ Icon, background, color }) {
    return (
        <Box
            sx={{
                width: 40,
                height: 40,
                flex: 'none',
                borderRadius: '12px',
                background,
                color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Icon sx={{ fontSize: 20 }} />
        </Box>
    );
}

const MILESTONE_STATUSES = [
    { key: 'billable', label: 'Billable', badgeClass: 'badge-green', barClass: 'green', inPipelinePct: true },
    { key: 'pending', label: 'Pending', badgeClass: 'badge-gray', barClass: 'gray', inPipelinePct: true },
    { key: 'held', label: 'Held', badgeClass: 'badge-yellow', barClass: 'orange', inPipelinePct: true },
    { key: 'forfeited', label: 'Forfeited', badgeClass: 'badge-red', barClass: 'red', inPipelinePct: true },
    { key: 'invoiced', label: 'Invoiced', badgeClass: 'badge-purple', barClass: 'green', inPipelinePct: false },
];

const STATUS_CHIP_COLOR = {
    billable: 'success',
    pending: 'default',
    held: 'warning',
    forfeited: 'error',
    invoiced: 'secondary',
};

const PIPELINE_STATUS_KEYS = MILESTONE_STATUSES.filter((status) => status.inPipelinePct).map((status) => status.key);
const ACTIVE_STATUS_KEYS = ['billable', 'pending', 'held'];

const EMPTY_BUCKET = { count: 0, amount: 0 };

const SUB_AGENT_PAGE_LIMIT = 50;

async function fetchAllSubAgents() {
    const firstResponse = await axiosInstance.get('/fibe-billing/sub-agents/dashboard/summary', {
        params: { page: 1, limit: SUB_AGENT_PAGE_LIMIT },
    });
    const { data: firstPageAgents, meta } = firstResponse.data;
    if (meta.totalPages <= 1) {
        return firstPageAgents;
    }

    const remainingPages = Array.from({ length: meta.totalPages - 1 }, (_, index) => index + 2);
    const remainingResponses = await Promise.all(
        remainingPages.map((page) =>
            axiosInstance.get('/fibe-billing/sub-agents/dashboard/summary', { params: { page, limit: SUB_AGENT_PAGE_LIMIT } }),
        ),
    );
    return firstPageAgents.concat(...remainingResponses.map((response) => response.data.data));
}

function RefreshGlyph() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <path d="M21 4v5h-5" />
        </svg>
    );
}

function DocumentGlyph() {
    return (
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2Z" />
            <path d="M9 9h6M9 13h4" />
        </svg>
    );
}

function PageHeader({ isLoading, onRefresh }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: `${fibeTokens.spacing.lg}px` }}>
            <Box>
                <Box
                    component="h1"
                    sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.pageTitle, color: fibeTokens.color.textPrimary }}
                >
                    Dashboard Overview
                </Box>
            </Box>
            <Button
                variant="fibeSecondary"
                disabled={isLoading}
                onClick={onRefresh}
                startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshGlyph />}
            >
                {isLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
        </Box>
    );
}

function ErrorBanner({ message, onRetry }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '14px',
                background: fibeTokens.color.dangerTint,
                border: `1px solid ${fibeTokens.color.dangerBorder}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                padding: fibeTokens.density.cardPadding,
                marginBottom: '16px',
            }}
        >
            <Box>
                <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.danger }}>
                    Could not load dashboard data
                </Box>
                <Box sx={{ ...fibeTokens.font.helper, fontFamily: fibeTokens.font.family, color: fibeTokens.color.danger, marginTop: '4px' }}>
                    {message}
                </Box>
            </Box>
            <Box sx={{ flex: 'none' }}>
                <Button variant="fibeSecondary" size="small" onClick={onRetry}>Retry</Button>
            </Box>
        </Box>
    );
}

function EmptyState({ onNavigateToTab }) {
    return (
        <Box
            sx={{
                textAlign: 'center',
                background: fibeTokens.color.surface,
                border: fibeTokens.density.cardBorder,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: fibeTokens.density.cardShadow,
                padding: '40px 18px',
            }}
        >
            <Box
                sx={{
                    width: fibeTokens.iconContainer.size,
                    height: fibeTokens.iconContainer.size,
                    margin: '0 auto 12px',
                    borderRadius: `${fibeTokens.iconContainer.radius}px`,
                    background: fibeTokens.iconContainer.tones.grey.background,
                    color: fibeTokens.iconContainer.tones.grey.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <DocumentGlyph />
            </Box>
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.textPrimary }}>
                No Fibe activity yet
            </Box>
            <Box
                sx={{
                    fontFamily: fibeTokens.font.family,
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: 1.6,
                    color: fibeTokens.color.textMuted,
                    margin: '5px 0 13px',
                }}
            >
                Commissions and invoices will appear here once loans are reconciled and billed.
            </Box>
            <Button variant="fibePrimary" size="small" onClick={() => onNavigateToTab?.('invoices')}>
                Generate invoice
            </Button>
        </Box>
    );
}

function SummaryCardSkeleton() {
    return (
        <Box sx={{ ...summaryCardSx, background: fibeTokens.color.surface, '&:hover': undefined }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Skeleton variant="text" width={100} height={16} />
                <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: '12px', flex: 'none' }} />
            </Box>
            <Skeleton variant="text" width={120} height={30} sx={{ marginTop: `${fibeTokens.spacing.sm}px` }} />
            <Skeleton variant="text" width={90} height={13} sx={{ marginTop: `${fibeTokens.spacing.xs}px` }} />
        </Box>
    );
}

function OverviewLoadingSkeleton() {
    return (
        <div>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: `${fibeTokens.density.gridGap}px`, marginBottom: '14px' }}>
                {[0, 1, 2, 3].map((i) => <SummaryCardSkeleton key={i} />)}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: `${fibeTokens.density.gridGap}px`, marginBottom: '16px' }}>
                {[0, 1, 2, 3].map((i) => <SummaryCardSkeleton key={i} />)}
            </Box>
            <Box
                sx={{
                    background: fibeTokens.color.surface,
                    border: fibeTokens.density.cardBorder,
                    borderRadius: `${fibeTokens.density.cardRadius}px`,
                    boxShadow: fibeTokens.density.cardShadow,
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ padding: fibeTokens.density.cardHeaderPadding, borderBottom: `1px solid ${fibeTokens.color.border}` }}>
                    <Skeleton variant="text" width={190} height={16} />
                </Box>
                <Box sx={{ padding: '0 18px' }}>
                    {MILESTONE_STATUSES.map((status, index) => (
                        <Box
                            key={status.key}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '18px',
                                height: `${fibeTokens.density.tableRowHeight}px`,
                                borderBottom: index < MILESTONE_STATUSES.length - 1 ? `1px solid ${fibeTokens.color.border}` : 'none',
                            }}
                        >
                            <Skeleton variant="rounded" width={72} height={20} sx={{ borderRadius: '999px' }} />
                            <Skeleton variant="text" width={30} height={12} />
                            <Skeleton variant="text" width={80} height={12} />
                            <Skeleton variant="text" width={34} height={12} />
                            <Skeleton variant="rounded" width={200} height={6} sx={{ borderRadius: '999px', marginLeft: 'auto' }} />
                        </Box>
                    ))}
                </Box>
            </Box>
        </div>
    );
}

function OverviewTab({ onNavigateToTab }) {
    const [invoiceSummary, setInvoiceSummary] = useState(null);
    const [payoutSummary, setPayoutSummary] = useState(null);
    const [commissionSummary, setCommissionSummary] = useState(null);
    const [subAgentTotals, setSubAgentTotals] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);

    const loadOverviewData = useCallback(() => {
        setIsLoading(true);
        setErrorMessage(null);

        Promise.all([
            axiosInstance.get('/fibe-billing/invoices/dashboard/summary'),
            axiosInstance.get('/fibe-billing/payouts/dashboard/summary'),
            axiosInstance.get('/fibe-billing/commissions/dashboard/summary'),
            fetchAllSubAgents(),
        ])
            .then(([invoiceRes, payoutRes, commissionRes, agents]) => {
                setInvoiceSummary(invoiceRes.data.data);
                setPayoutSummary(payoutRes.data.data);
                setCommissionSummary(commissionRes.data.data);

                setSubAgentTotals({
                    totalPaid: agents.reduce((sum, agent) => sum + Number(agent.totalPaid), 0),
                    totalPending: agents.reduce((sum, agent) => sum + Number(agent.totalPayable), 0),
                });
            })
            .catch(() => setErrorMessage('Could not load dashboard data. Please try refreshing.'))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        loadOverviewData();
    }, [loadOverviewData]);

    const hasData = invoiceSummary !== null;

    let content;

    if (!hasData && isLoading) {
        content = <OverviewLoadingSkeleton />;
    } else if (!hasData && errorMessage) {
        content = <ErrorBanner message={errorMessage} onRetry={loadOverviewData} />;
    } else if (hasData) {
        const pendingInvoiceCount = invoiceSummary.invoiceCount - invoiceSummary.fullySettledCount;
        const collectionRate = invoiceSummary.totalInvoiced > 0
            ? (invoiceSummary.totalSettled / invoiceSummary.totalInvoiced) * 100
            : 0;

        const totalPipelineAmount = PIPELINE_STATUS_KEYS.reduce(
            (sum, key) => sum + (payoutSummary[key]?.amount ?? 0),
            0,
        );
        const totalPipelineCount = PIPELINE_STATUS_KEYS.reduce(
            (sum, key) => sum + (payoutSummary[key]?.count ?? 0),
            0,
        );
        const totalActiveMilestoneCount = ACTIVE_STATUS_KEYS.reduce(
            (sum, key) => sum + (payoutSummary[key]?.count ?? 0),
            0,
        );

        const isEmpty = invoiceSummary.invoiceCount === 0 && commissionSummary.totalCount === 0;

        const primaryKpis = [
            {
                key: 'invoiced',
                label: 'Total invoiced to Fibe',
                value: formatCurrency(invoiceSummary.totalInvoiced),
                sub: [`${invoiceSummary.invoiceCount} invoices generated`],
                Icon: ReceiptLongRoundedIcon,
                tone: 'green',
                iconBg: fibeTokens.iconContainer.tones.green.background,
                iconColor: fibeTokens.iconContainer.tones.green.color,
            },
            {
                key: 'received',
                label: 'Total received',
                value: formatCurrency(invoiceSummary.totalSettled),
                sub: [`${collectionRate.toFixed(1)}% collection rate`],
                Icon: PaymentsRoundedIcon,
                tone: 'blue',
                iconBg: fibeTokens.iconContainer.tones.blue.background,
                iconColor: fibeTokens.iconContainer.tones.blue.color,
            },
            {
                key: 'outstanding',
                label: 'Outstanding from Fibe',
                value: formatCurrency(invoiceSummary.totalOutstanding),
                sub: [`${pendingInvoiceCount} invoices pending`],
                Icon: AccountBalanceWalletRoundedIcon,
                tone: 'amber',
                iconBg: fibeTokens.iconContainer.tones.amber.background,
                iconColor: fibeTokens.iconContainer.tones.amber.color,
            },
            {
                key: 'held',
                label: 'Held (30+ DPD)',
                value: formatCurrency(payoutSummary.held.amount),
                sub: [`${payoutSummary.held.count} milestones held`],
                Icon: WarningAmberRoundedIcon,
                tone: 'red',
                iconBg: fibeTokens.iconContainer.tones.red.background,
                iconColor: fibeTokens.iconContainer.tones.red.color,
            },
        ];

        const secondaryKpis = [
            {
                key: 'commissions',
                label: 'Commissions tracked',
                value: commissionSummary.totalCount,
                sub: [
                    `${commissionSummary.countByStatus.calculated} calculated`,
                    `${commissionSummary.countByStatus.pending_approval} pending approval`,
                    `${commissionSummary.countByStatus.pending_reconciliation} pending reconciliation`,
                ],
                Icon: InsightsRoundedIcon,
                tone: 'purple',
                iconBg: fibeTokens.iconContainer.tones.purple.background,
                iconColor: fibeTokens.iconContainer.tones.purple.color,
            },
            {
                key: 'subAgentPayable',
                label: 'Sub-agent payable',
                value: formatCurrency(subAgentTotals.totalPaid + subAgentTotals.totalPending),
                sub: [
                    `${formatCurrency(subAgentTotals.totalPaid)} paid`,
                    `${formatCurrency(subAgentTotals.totalPending)} pending`,
                ],
                Icon: GroupsRoundedIcon,
                tone: 'teal',
                iconBg: fibeTokens.iconContainer.tones.teal.background,
                iconColor: fibeTokens.iconContainer.tones.teal.color,
            },
            {
                key: 'avgCommission',
                label: 'Avg FeeMonk commission',
                value: formatPercent(commissionSummary.avgFeemonkSharePct),
                sub: [`IRR range: ${formatPercent(commissionSummary.minIrr)} - ${formatPercent(commissionSummary.maxIrr)}`],
                Icon: PercentRoundedIcon,
                tone: 'grey',
                iconBg: fibeTokens.iconContainer.tones.grey.background,
                iconColor: fibeTokens.iconContainer.tones.grey.color,
            },
            {
                key: 'activeMilestones',
                label: 'Active milestones',
                value: totalActiveMilestoneCount,
                sub: [
                    `${payoutSummary.billable.count} billable`,
                    `${payoutSummary.pending.count} pending`,
                    `${payoutSummary.held.count} held`,
                ],
                Icon: TaskAltRoundedIcon,
                tone: 'rose',
                iconBg: fibeTokens.iconContainer.tones.rose.background,
                iconColor: fibeTokens.iconContainer.tones.rose.color,
            },
        ];

        content = (
            <>
                {errorMessage && <ErrorBanner message={errorMessage} onRetry={loadOverviewData} />}
                {isEmpty ? (
                    <EmptyState onNavigateToTab={onNavigateToTab} />
                ) : (
                    <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: `${fibeTokens.density.gridGap}px`, marginBottom: `${fibeTokens.spacing.md}px` }}>
                            {primaryKpis.map((kpi) => (
                                <Box key={kpi.key} sx={{ ...summaryCardSx, background: CARD_GRADIENTS[kpi.tone] }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                                        <Box sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.cardTitle, color: fibeTokens.color.textSecondary }}>{kpi.label}</Box>
                                        <SummaryCardIcon Icon={kpi.Icon} background={kpi.iconBg} color={kpi.iconColor} />
                                    </Box>
                                    <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 700, fontSize: 26, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums', color: fibeTokens.color.textPrimary, marginTop: `${fibeTokens.spacing.sm}px` }}>
                                        {kpi.value}
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: `${fibeTokens.spacing.xs}px` }}>
                                        {kpi.sub.map((line, index) => (
                                            <Box
                                                key={index}
                                                component="p"
                                                sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.caption, margin: 0, padding: 0 }}
                                            >
                                                {kpi.sub.length > 1 ? `• ${line}` : line}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: `${fibeTokens.density.gridGap}px`, marginBottom: `${fibeTokens.spacing.lg}px` }}>
                            {secondaryKpis.map((kpi) => (
                                <Box key={kpi.key} sx={{ ...summaryCardSx, background: CARD_GRADIENTS[kpi.tone] }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                                        <Box sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.cardTitle, color: fibeTokens.color.textSecondary }}>{kpi.label}</Box>
                                        <SummaryCardIcon Icon={kpi.Icon} background={kpi.iconBg} color={kpi.iconColor} />
                                    </Box>
                                    <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 700, fontSize: 26, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums', color: fibeTokens.color.textPrimary, marginTop: `${fibeTokens.spacing.sm}px` }}>
                                        {kpi.value}
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: `${fibeTokens.spacing.xs}px` }}>
                                        {kpi.sub.map((line, index) => (
                                            <Box
                                                key={index}
                                                component="p"
                                                sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.caption, margin: 0, padding: 0 }}
                                            >
                                                {kpi.sub.length > 1 ? `• ${line}` : line}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Box
                            sx={{
                                background: fibeTokens.color.surface,
                                border: fibeTokens.density.cardBorder,
                                borderRadius: `${fibeTokens.density.cardRadius}px`,
                                boxShadow: fibeTokens.density.cardShadow,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: fibeTokens.density.cardHeaderPadding,
                                    borderBottom: `1px solid ${fibeTokens.color.border}`,
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    <Box
                                        component="h2"
                                        sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.sectionHeading, color: fibeTokens.color.textPrimary }}
                                    >
                                        Payout Milestone Pipeline
                                    </Box>
                                    <Box sx={{ fontFamily: fibeTokens.font.family, fontSize: 11.5, color: fibeTokens.color.textDisabled }}>
                                        Current pipeline mix. Invoiced is an all-time total and is excluded from the percentages.
                                    </Box>
                                </Box>
                                <Box sx={{ flex: 1 }} />
                                <Button variant="fibePrimary" size="small" onClick={() => onNavigateToTab?.('invoices')}>
                                    Generate invoice →
                                </Button>
                            </Box>
                            <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell size="fibe" variant="head" align="left" sx={{ width: '15%' }}>Status</TableCell>
                                        <TableCell size="fibe" variant="head" align="right" sx={{ width: '12%' }}>Count</TableCell>
                                        <TableCell size="fibe" variant="head" align="right" sx={{ width: '20%' }}>Amount</TableCell>
                                        <TableCell size="fibe" variant="head" align="right" sx={{ width: '13%' }}>% of pipeline</TableCell>
                                        <TableCell size="fibe" variant="head" align="left" sx={{ width: '40%', padding: '0 16px 0 20px' }}>Distribution</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {PIPELINE_STATUS_KEYS.map((key) => {
                                        const status = MILESTONE_STATUSES.find((item) => item.key === key);
                                        const bucket = payoutSummary[key] ?? EMPTY_BUCKET;
                                        const pctOfTotal = totalPipelineAmount > 0 ? (bucket.amount / totalPipelineAmount) * 100 : 0;
                                        return (
                                            <TableRow key={key} hover>
                                                <TableCell size="fibe" variant="body" align="left" sx={{ width: '15%' }}>
                                                    <Chip variant="fibePill" color={STATUS_CHIP_COLOR[key]} label={status.label} />
                                                </TableCell>
                                                <TableCell size="fibe" variant="body" align="right" sx={{ width: '12%', fontVariantNumeric: 'tabular-nums' }}>
                                                    {bucket.count}
                                                </TableCell>
                                                <TableCell size="fibe" variant="body" align="right" sx={{ width: '20%', fontWeight: 500, color: fibeTokens.color.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                                                    {formatCurrency(bucket.amount)}
                                                </TableCell>
                                                <TableCell size="fibe" variant="body" align="right" sx={{ width: '13%', color: fibeTokens.color.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                                                    {pctOfTotal.toFixed(0)}%
                                                </TableCell>
                                                <TableCell size="fibe" variant="body" align="left" sx={{ width: '40%', padding: '0 16px 0 20px' }}>
                                                    <Box sx={{ height: '6px', width: '100%', background: fibeTokens.color.trackBg, borderRadius: '999px', overflow: 'hidden', border: `1px solid ${fibeTokens.color.border}` }}>
                                                        <Box sx={{ height: '100%', width: `${pctOfTotal}%`, background: fibeTokens.pipelineBar[key], borderRadius: '999px', transition: 'width 0.3s ease' }} />
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    <TableRow>
                                        <TableCell
                                            size="fibe"
                                            variant="body"
                                            align="left"
                                            sx={{ width: '15%', background: fibeTokens.color.rowHover, borderTop: `1px solid ${fibeTokens.color.border}`, fontWeight: 600, fontSize: 11.5, color: fibeTokens.color.textSecondary }}
                                        >
                                            Pipeline total
                                        </TableCell>
                                        <TableCell
                                            size="fibe"
                                            variant="body"
                                            align="right"
                                            sx={{ width: '12%', background: fibeTokens.color.rowHover, borderTop: `1px solid ${fibeTokens.color.border}`, fontWeight: 600, color: fibeTokens.color.textPrimary, fontVariantNumeric: 'tabular-nums' }}
                                        >
                                            {totalPipelineCount}
                                        </TableCell>
                                        <TableCell
                                            size="fibe"
                                            variant="body"
                                            align="right"
                                            sx={{ width: '20%', background: fibeTokens.color.rowHover, borderTop: `1px solid ${fibeTokens.color.border}`, fontWeight: 600, color: fibeTokens.color.textPrimary, fontVariantNumeric: 'tabular-nums' }}
                                        >
                                            {formatCurrency(totalPipelineAmount)}
                                        </TableCell>
                                        <TableCell
                                            size="fibe"
                                            variant="body"
                                            align="right"
                                            sx={{ width: '13%', background: fibeTokens.color.rowHover, borderTop: `1px solid ${fibeTokens.color.border}`, fontWeight: 600, color: fibeTokens.color.textPrimary, fontVariantNumeric: 'tabular-nums' }}
                                        >
                                            100%
                                        </TableCell>
                                        <TableCell
                                            size="fibe"
                                            variant="body"
                                            align="left"
                                            sx={{ width: '40%', background: fibeTokens.color.rowHover, borderTop: `1px solid ${fibeTokens.color.border}`, padding: '0 16px 0 20px' }}
                                        >
                                            <Box sx={{ display: 'flex', height: '6px', width: '100%', borderRadius: '999px', overflow: 'hidden', border: `1px solid ${fibeTokens.color.border}` }}>
                                                {PIPELINE_STATUS_KEYS.map((key) => {
                                                    const bucket = payoutSummary[key] ?? EMPTY_BUCKET;
                                                    const share = totalPipelineAmount > 0 ? (bucket.amount / totalPipelineAmount) * 100 : 0;
                                                    return <Box key={key} sx={{ height: '100%', width: `${share}%`, background: fibeTokens.pipelineBar[key] }} />;
                                                })}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {(() => {
                                        const invoicedStatus = MILESTONE_STATUSES.find((item) => !item.inPipelinePct);
                                        const invoicedBucket = payoutSummary[invoicedStatus.key] ?? EMPTY_BUCKET;
                                        return (
                                            <TableRow hover>
                                                <TableCell size="fibe" variant="body" align="left" sx={{ width: '15%' }}>
                                                    <Chip variant="fibePill" color={STATUS_CHIP_COLOR[invoicedStatus.key]} label={invoicedStatus.label} />
                                                </TableCell>
                                                <TableCell size="fibe" variant="body" align="right" sx={{ width: '12%', fontVariantNumeric: 'tabular-nums' }}>
                                                    {invoicedBucket.count}
                                                </TableCell>
                                                <TableCell size="fibe" variant="body" align="right" sx={{ width: '20%', fontWeight: 500, color: fibeTokens.color.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                                                    {formatCurrency(invoicedBucket.amount)}
                                                </TableCell>
                                                <TableCell size="fibe" variant="body" align="right" sx={{ width: '13%', color: fibeTokens.color.textDisabled }}>
                                                    —
                                                </TableCell>
                                                <TableCell size="fibe" variant="body" align="left" sx={{ width: '40%', padding: '0 16px 0 20px', color: fibeTokens.color.textDisabled, fontSize: 11.5 }}>
                                                    All-time, not part of the current mix
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })()}
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                )}
            </>
        );
    } else {
        content = null;
    }

    return (
        <div>
            <PageHeader isLoading={isLoading} onRefresh={loadOverviewData} />
            {content}
        </div>
    );
}

export default OverviewTab;